import type { CleanEvent } from '@axiom/ui';
import { Api } from '@axiom/ui';
import type {
  Calendar,
  CalendarEventType,
  CalendarScheduledInterview,
  Candidate,
  Contact,
  TimeZoneIana,
} from '@axiom/validation';

import { EnvUtil } from '../utils/env-util';

class CalendarApiClass extends Api {
  constructor() {
    super({
      domain: EnvUtil.apiEnvoyUrl,
    });
  }

  async createCalendar(body: {
    candidateId?: Candidate['id'];
    contactId?: Contact['id'];
    timezone: TimeZoneIana;
  }) {
    return super.write<{
      data: {
        id: Calendar['id'];
        timezone: TimeZoneIana;
      };
    }>({
      endpoint: `/calendars`,
      method: 'POST',
      body,
    });
  }

  readCalendar(calendarId: Calendar['id']) {
    return super.read<{ data: Calendar }>({
      endpoint: `/calendars/${calendarId}`,
      method: 'GET',
    });
  }

  async updateCalendar(
    calendarId: Calendar['id'],
    body: { timezone: TimeZoneIana }
  ) {
    return super.write<{
      data: {
        id: Calendar['id'];
        timezone: TimeZoneIana;
      };
    }>({
      endpoint: `/calendars/${calendarId}`,
      method: 'PATCH',
      body,
    });
  }

  async refreshCalendar(calendarId: Calendar['id']) {
    return super.invalidate(`/calendars/${calendarId}`);
  }

  async createCalendarEvent(
    calendarId: Calendar['id'],
    body: CalendarEventType
  ) {
    return super.write<{ data: Calendar }>({
      endpoint: `/calendars/${calendarId}/events`,
      method: 'POST',
      body,
    });
  }

  async createCalendarEvents(calendarId: Calendar['id'], body: CleanEvent[]) {
    return super.write<{ data: Calendar }>({
      endpoint: `/calendars/${calendarId}/batchEvents`,
      method: 'POST',
      body,
    });
  }

  async refreshCalendarEvents(calendarId: Calendar['id']) {
    return super.invalidate(`/calendars/${calendarId}/events`);
  }

  async updateCalendarEvent(
    calendarId: Calendar['id'],
    eventId: CalendarEventType['id'],
    body: CalendarEventType
  ) {
    return super.write<{ data: Calendar }>({
      endpoint: `/calendars/${calendarId}/events/${eventId}`,
      method: 'PATCH',
      body,
    });
  }

  async updateCalendarEvents(
    calendarId: Calendar['id'],
    body: {
      [key: NonNullable<CalendarEventType['id']>]: CleanEvent;
    }
  ) {
    return super.write<{ data: Calendar }>({
      endpoint: `/calendars/${calendarId}/batchEvents`,
      method: 'PATCH',
      body,
    });
  }

  async deleteCalendarEvent(
    calendarId: Calendar['id'],
    eventId: CalendarEventType['id']
  ) {
    return super.write<{ data: Calendar }>({
      endpoint: `/calendars/${calendarId}/events/${eventId}`,
      method: 'DELETE',
    });
  }

  async deleteCalendarEvents(
    calendarId: Calendar['id'],
    body: Array<CalendarEventType['id']>
  ) {
    return super.write<{ data: Calendar }>({
      endpoint: `/calendars/${calendarId}/batchEvents`,
      method: 'DELETE',
      body,
    });
  }

  async createScheduledCalendarInterview(body: CalendarScheduledInterview) {
    return super.write<{ data: Calendar }>({
      endpoint: `/calendars/schedule`,
      method: 'POST',
      body,
    });
  }
}

export const CalendarApi = new CalendarApiClass();
